import { trace } from '@opentelemetry/api';

import { logError } from './logger.mjs';
import getCacheStrategy from './server/getCacheStrategy';

const getDebugLabel = (url, options) => {
  const { method, body } = options;

  if (body) {
    try {
      const bodyObject = JSON.parse(body || '{}');

      const isQuery = bodyObject.query?.startsWith('query ');
      const isMutation = bodyObject.query?.startsWith('mutation ');
      const operationName = bodyObject.operationName;

      if (isQuery && operationName) {
        return `query ${operationName}`;
      }

      if (isMutation && operationName) {
        return `mutation ${operationName}`;
      }
    } catch {
      return `${method} ${url}`;
    }
  }

  return `${method} ${url}`;
};

export const fetchWithRetry = async (url, options) => {
  const debugLabel = getDebugLabel(url, options);
  const { cache = 'force-cache', revalidate = 3600 } = options;

  // Determine cache strategy based on request context
  const cacheStrategy = getCacheStrategy(options, cache);

  try {
    return await trace
      .getTracer('GraphQL request')
      .startActiveSpan(debugLabel, async span => {
        try {
          return await fetch(url, {
            ...options,
            cache: cacheStrategy,
            revalidate,
          });
        } catch (err) {
          if (err.name === 'AbortError') {
            span.addEvent('Request aborted');
            return null;
          }

          const error = new Error(
            `fetchWithRetry: Request error: ${debugLabel}`,
            {
              cause: err,
            }
          );

          logError(error);

          span.recordException(error);
          span.setStatus({ code: 2, message: error.message });
          throw error;
        } finally {
          span.end();
        }
      });
  } catch (err) {
    if (err.name === 'AbortError') {
      span.addEvent('Request aborted');
      return null;
    }

    if (/ETIMEDOUT|ECONNRESET|Network Error/.test(err.message)) {
      try {
        return await trace
          .getTracer('GraphQL request')
          .startActiveSpan(debugLabel, async span => {
            try {
              return await fetch(url, {
                ...options,
                cache: cacheStrategy,
                revalidate,
              });
            } catch (err) {
              if (err.name === 'AbortError') {
                span.addEvent('Request aborted');
                return null;
              }

              const error = new Error(
                `fetchWithRetry: Request error: ${debugLabel}`,
                {
                  cause: err,
                }
              );

              logError(error);

              span.recordException(error);
              span.setStatus({ code: 2, message: error.message });
              throw error;
            } finally {
              span.end();
            }
          });
      } catch (retryErr) {
        const retryError = new Error(
          `fetchWithRetry: Request error: ${debugLabel}`,
          {
            cause: retryErr,
          }
        );

        throw retryError;
      }
    }

    throw err;
  }
};
